import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Plus, Trash2, Upload, Link } from 'lucide-react';
import '../admin/admin_content_update_form.css'; // Reusing your global Admin framework styles
import { useParams, useNavigate } from 'react-router-dom';

const AdminContentForm = ({ type = 'event' }) => {
  const { id } = useParams(); // 🟢 Checks if there is an ID passed in the URL string context
  const navigate = useNavigate();
  const isEditMode = !!id; 

  const isEvent = type === 'event';
  const collectionName = isEvent ? 'parmEvent' : 'parmNews';
  const prefix = isEvent ? 'event' : 'news';

  const [loading, setLoading] = useState(false);
  const [docIdDisplay, setDocIdDisplay] = useState('');
  const [docNoDisplay, setDocNoDisplay] = useState(1);

  const [formData, setFormData] = useState({
    title: '', description: '', date: '', venue: '', time: '', isMainPage: false, isTimelinePage: false, status: 'published', category: ''
  });
  const [images, setImages] = useState(['']); 
  const [imageTitles, setImageTitles] = useState(['']);

  // 🟢 HOOK: Decides whether to auto-generate a new numerical ID index or pull existing record
  useEffect(() => {
    const initializeForm = async () => {
      if (isEditMode) {
        // Mode A: Fetch existing structural draft values
        try {
          const docRef = doc(db, collectionName, id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDocIdDisplay(id);
            setDocNoDisplay(data[`${prefix}_no`] || 1);
            setFormData({
              title: data[`${prefix}_title`] || '',
              description: data[`${prefix}_description`] || '',
              date: data[`${prefix}_date`] || '',
              venue: data[`${prefix}_venue`] || '',
              time: data[`${prefix}_time`] || '',
              isMainPage: data[`${prefix}_main_page`] || false,
              isTimelinePage: data[`${prefix}_timeline_page`] || false,
              status: data.status || 'published',
              category: data.category || ''
            });
            setImages(data[`${prefix}_image_array`]?.length ? data[`${prefix}_image_array`] : ['']);
            setImageTitles(data[`${prefix}_image_title_array`]?.length ? data[`${prefix}_image_title_array`] : ['']);
          }
        } catch (err) {
          console.error("Failed to recover draft asset index structures:", err);
        }
      } else {
        // Mode B: Fetch count matrix size to build next serial code string (Original flow)
        const querySnapshot = await getDocs(collection(db, collectionName));
        const nextNo = querySnapshot.size + 1;
        setDocIdDisplay(`${prefix}_${String(nextNo).padStart(3, '0')}`);
        setDocNoDisplay(nextNo);
      }
    };
    initializeForm();
  }, [id, isEditMode, collectionName, prefix]);

  const handleInputChange = (e) => {
    const { name, value, type: t, checked } = e.target;
    setFormData(p => ({ ...p, [name]: t === 'checkbox' ? checked : value }));
  };

  const handleImageArrayChange = (index, val) => {
    const updated = [...images]; updated[index] = val; setImages(updated);
  };
  const handleImageTitleArrayChange = (index, val) => {
    const updated = [...imageTitles]; updated[index] = val; setImageTitles(updated);
  };
  const addImageRow = () => { setImages([...images, '']); setImageTitles([...imageTitles, '']); };
  const removeImageRow = (index) => {
    if (images.length === 1) return;
    setImages(images.filter((_, i) => i !== index));
    setImageTitles(imageTitles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const docRef = doc(db, collectionName, docIdDisplay);
      const cleanImages = images.filter(url => url.trim() !== '');
      const cleanTitles = imageTitles.filter((_, idx) => images[idx].trim() !== '');
      const slug = formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      const payload = {
        [`${prefix}_title`]: formData.title,
        [`${prefix}_description`]: formData.description,
        [`${prefix}_image_array`]: cleanImages,
        [`${prefix}_image_title_array`]: cleanTitles,
        [`${prefix}_main_page`]: formData.isMainPage,
        [`${prefix}_timeline_page`]: formData.isTimelinePage,
        status: formData.status,
        category: formData.category,
        slug: slug,
        updatedAt: serverTimestamp(),
        ...(isEvent && {
          [`${prefix}_date`]: formData.date,
          [`${prefix}_venue`]: formData.venue,
          [`${prefix}_time`]: formData.time,
        })
      };

      if (isEditMode) {
        // Update operational target record context
        await updateDoc(docRef, payload);
        alert("🎉 Content entry successfully modified!");
      } else {
        // Set completely new record entry lines
        payload[`${prefix}_id`] = docIdDisplay;
        payload[`${prefix}_no`] = docNoDisplay;
        payload.createdAt = serverTimestamp();
        await setDoc(docRef, payload);
        alert("🎉 New item published successfully!");
      }

      navigate(`/admin-parm/content-manage-parm/${prefix}`); // Back to lists interface
    } catch (err) {
      console.error(err);
      alert("Error writing log data parameters to database.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-member-page">
      <div className="admin-member-container" style={{ maxWidth: '800px' }}>
        <header className="page-header">
          <h2>{isEditMode ? 'Modify Saved Draft' : `Create New Entry`}</h2>
          <p>Modify fields below to either maintain draft states or update to published live fields.</p>
          </header>

        <form onSubmit={handleSubmit} className="admin-form-wrapper">
          
          {/* Automatic Meta Tracking row */}
          <div className="admin-form-row-grid-2">
            <div 
            className="admin-form-group-box">
              <label>ID Target</label>
              <input type="text" className="admin-table-inline-input" value={docIdDisplay} disabled style={{ background: '#f1f5f9' }} />
            </div>
            <div 
            className="admin-form-group-box">
              <label>Sort Index No</label>
              <input type="text" className="admin-table-inline-input" value={docNoDisplay} disabled style={{ background: '#f1f5f9' }} />
            </div>
          </div>

          {/* Core Content String Fields */}
          <div className="admin-form-group-box">
            <label>Title</label>
            <input 
              type="text" name="title" className="admin-table-inline-input" required
              placeholder={`Enter operational ${prefix} title headline...`}
              value={formData.title} onChange={handleInputChange} 
            />
          </div>

          <div className="admin-form-row-grid-2">
            <div className="admin-form-group-box">
              <label>Category Group</label>
              <select name="category" className="admin-table-inline-select" value={formData.category} onChange={handleInputChange}>
                <option value="">Select Category</option>
                {isEvent ? (
                  <>
                    <option value="Conference">Conference</option>
                    <option value="Webinar">Webinar</option>
                    <option value="Workshop">Workshop</option>
                  </>
                ) : (
                  <>
                    <option value="Announcement">Announcement</option>
                    <option value="Press Release">Press Release</option>
                    <option value="Newsletter">Newsletter</option>
                  </>
                )}
              </select>
            </div>
            <div className="admin-form-group-box">
              <label>Publication Visibility State</label>
              <select name="status" className="admin-table-inline-select" value={formData.status} onChange={handleInputChange}>
                <option value="published">Live (Published)</option>
                <option value="draft">Draft (Hidden)</option>
              </select>
            </div>
          </div>

          {/* Conditional Event Logistics Blocks */}
          {isEvent && (
            <div className="admin-form-row-grid-3">
              <div className="admin-form-group-box">
                <label>Event Date</label>
                <input type="date" name="date" className="admin-table-inline-input" value={formData.date} onChange={handleInputChange} />
              </div>
              <div className="admin-form-group-box">
                <label>Event Time Schedule</label>
                <input type="text" name="time" className="admin-table-inline-input" placeholder="e.g., 9:00 AM - 5:00 PM" value={formData.time} onChange={handleInputChange} />
              </div>
              <div className="admin-form-group-box">
                <label>Event Venue / Location</label>
                <input type="text" name="venue" className="admin-table-inline-input" placeholder="e.g., Grand Hall / Zoom" value={formData.venue} onChange={handleInputChange} />
              </div>
            </div>
          )}

          <div className="admin-form-group-box">
            <label>Detailed Description Body Text</label>
            <textarea 
              name="description" className="admin-table-inline-input" rows={6}
              placeholder="Compile rich structural narrative content layout fields here..."
              value={formData.description} onChange={handleInputChange}
              style={{ padding: '12px', resize: 'vertical' }}
            />
          </div>

          {/* Dynamic Image Array Repeater Engine */}
          <div className="admin-form-array-repeater-section">
            <div className="admin-repeater-header-axis">
              <label>Media Asset Image Gallery Setup</label>
              <button type="button" className="admin-act-btn btn-edit" onClick={addImageRow} style={{ padding: '4px 12px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                <Plus size={14} /> Add Image Slot
              </button>
            </div>

            {images.map((imgUrl, index) => (
              <div key={index} className="admin-array-repeater-row admin-form-row-grid-2" style={{ alignItems: 'flex-end', background: '#f8fafc', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                <div className="admin-form-group-box" style={{ marginBottom: 0 }}>
                  <label>Image Resource Link / URL {index + 1}</label>
                  <input 
                    type="text" className="admin-table-inline-input" placeholder="Paste image host web link URL..."
                    value={imgUrl} onChange={(e) => handleImageArrayChange(index, e.target.value)} 
                  />
                </div>
                <div className="admin-form-group-box" style={{ marginBottom: 0, position: 'relative' }}>
                  <label>Alternative Title / Caption {index + 1}</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input 
                      type="text" className="admin-table-inline-input" placeholder="Meta title label..."
                      value={imageTitles[index]} onChange={(e) => handleImageTitleArrayChange(index, e.target.value)} 
                    />
                    <button type="button" className="admin-act-btn btn-cancel" onClick={() => removeImageRow(index)} style={{ padding: '10px' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Boolean Main Showcase Checkbox Flag */}
          <div className="admin-form-group-box checkbox-switch-row" style={{ flexDirection: 'row', gap: '12px', alignItems: 'center', margin: '20px 0', marginBottom: '0px' }}>
            <input 
              type="checkbox" id="isMainPage" name="isMainPage" 
              checked={formData.isMainPage} onChange={handleInputChange}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <label htmlFor="isMainPage" style={{ margin: 0, cursor: 'pointer' }}>
              <strong>Feature on Main Page</strong> (Flag maximum of 3 active elements)
            </label>
          </div>

          <div className="admin-form-group-box checkbox-switch-row" style={{ flexDirection: 'row', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <input 
              type="checkbox" id="isTimelinePage" name="isTimelinePage" 
              checked={formData.isTimelinePage} onChange={handleInputChange}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <label htmlFor="isTimelinePage" style={{ margin: 0, cursor: 'pointer' }}>
              <strong>Feature on Main Landing Timeline Showcase</strong> (Flag maximum of 3 active elements)
            </label>
          </div>


          <button type="submit" className="intac-download-btn" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
            {loading ? "Synchronizing Records..." : isEditMode ? "Save & Update Document Changes" : "Save & Publish New Item"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminContentForm;